// Import the functions you need from the SDKs you need
import { initializeApp } from "firebase/app";
import { initializeAppCheck, ReCaptchaV3Provider } from "firebase/app-check";
import {
  browserLocalPersistence,
  browserSessionPersistence,
  connectAuthEmulator,
  indexedDBLocalPersistence,
  initializeAuth,
} from "firebase/auth";
import {
  connectFirestoreEmulator,
  getFirestore,
  initializeFirestore,
  persistentLocalCache,
  persistentMultipleTabManager,
} from "firebase/firestore";
import { connectFunctionsEmulator, getFunctions } from "firebase/functions";
import { connectStorageEmulator, getStorage } from "firebase/storage";
import firebaseConfig from "./config";

const app = initializeApp(firebaseConfig);
const isDevelopment = process.env.NODE_ENV === "development";
const appCheckSiteKey = process.env.REACT_APP_FIREBASE_APP_CHECK_SITE_KEY;

// Passwordless callables enforce App Check. Local development uses Firebase's
// debug-token flow; production/preview deployments must provide the public
// reCAPTCHA site key. Initialization stays conditional so a keyless build (and
// every jest run, which has no env) still boots — but see `isCodeSignInAvailable`
// below: without a key the code callables reject every call, so the UI must not
// pretend that path works.
if (appCheckSiteKey) {
  if (isDevelopment) {
    (
      globalThis as typeof globalThis & {
        FIREBASE_APPCHECK_DEBUG_TOKEN?: boolean;
      }
    ).FIREBASE_APPCHECK_DEBUG_TOKEN = true;
  }
  initializeAppCheck(app, {
    provider: new ReCaptchaV3Provider(appCheckSiteKey),
    isTokenAutoRefreshEnabled: true,
  });
}

/** True when App Check can actually mint tokens for the enforcing callables. */
export const isAppCheckConfigured = Boolean(appCheckSiteKey);

/**
 * Whether to offer one-time-code sign-in at all.
 *
 * `requestLoginCode` / `verifyLoginCode` set `enforceAppCheck: true`, so with no
 * site key the client sends no App Check header and the backend rejects every
 * call as `unauthenticated`. A production deployment missing the key must fall
 * back to password sign-in rather than leading students into a dead end.
 * Admins intentionally have no password fallback, so provisioning this key is
 * a release requirement for new admin sessions.
 * Non-production stays enabled so local dev and tests exercise the real flow.
 */
export const isCodeSignInAvailable =
  isAppCheckConfigured || process.env.NODE_ENV !== "production";

// Deliberately NOT getAuth(): that installs browserPopupRedirectResolver, which
// makes Firebase load an auth helper from `${authDomain}/__/auth/iframe`. Our
// Vercel catch-all rewrite serves the React bundle at that path instead of the
// helper, so the gapi handshake never completes and Firebase retries forever —
// each retry booting the whole app inside the iframe. That produced ~12x
// page-load amplification and, on WebKit, exhausted IndexedDB
// ("Connection to Indexed Database server lost"), which takes down both auth
// persistence and the Firestore cache mid-session.
// We only ever sign in with email/password, custom tokens, and email links —
// none of which need the resolver — so omit it. The persistence chain mirrors
// getAuth()'s browser default so restricted-storage browsers (private mode,
// in-app WebViews) keep their fallbacks.
export const auth = initializeAuth(app, {
  persistence: [
    indexedDBLocalPersistence,
    browserLocalPersistence,
    browserSessionPersistence,
  ],
});
initializeFirestore(app, {
  ignoreUndefinedProperties: true,
  localCache: persistentLocalCache({
    tabManager: persistentMultipleTabManager(),
  }),
  // The Firestore emulator's Listen stream is unreliable over WebChannel —
  // rapid onSnapshot listener swaps (e.g. switching cases in the Success Queue)
  // intermittently never deliver their initial snapshot, leaving the UI stuck
  // on a spinner. Long-polling is robust against the emulator. Dev-only: prod
  // keeps auto-detected WebChannel, which doesn't have this problem.
  ...(isDevelopment ? { experimentalForceLongPolling: true } : {}),
});
export const db = getFirestore();

export const storage = getStorage(app);
export const functions = getFunctions(app);

if (process.env.NODE_ENV === "development") {
  connectFunctionsEmulator(functions, "localhost", 5006);
  connectFirestoreEmulator(db, "localhost", 8080);
  connectAuthEmulator(auth, "http://localhost:5005");
  connectStorageEmulator(storage, "localhost", 5008);
}
